import { useEffect, useState } from "react";

export default function Hero() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center overflow-hidden bg-black"
    >
      {/* B&W urban background — grayscale filtered to match comic art */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url("https://public.readdy.ai/ai/img_res/9c4b5e2e077d32f8f44ba20f0724ca54.jpg")`,
          filter: "grayscale(100%) contrast(1.25) brightness(0.55)",
        }}
      />

      {/* Directional overlays */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/60 to-black/25" />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/50" />

      {/* Subtle scanline texture for comic feel */}
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "repeating-linear-gradient(0deg, #fff, #fff 1px, transparent 1px, transparent 4px)",
        }}
      />

      <div className="relative z-10 w-full px-8 md:px-16 flex flex-col md:flex-row items-center gap-12">
        <div
          className={`flex-1 transition-all duration-1000 ${visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-12"}`}
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-0.5 bg-red-600" />
            <span className="font-rajdhani text-red-500 uppercase tracking-widest text-sm font-semibold">
              Gaming Legend
            </span>
          </div>

          <h1 className="font-bebas text-7xl md:text-9xl text-white leading-none tracking-wider mb-2">
            OLD MAN
          </h1>
          <h1 className="font-bebas text-7xl md:text-9xl text-red-600 leading-none tracking-wider mb-6">
            MURKS
          </h1>

          <p className="font-rajdhani text-gray-300 text-xl md:text-2xl font-semibold mb-2">
            Mike Barrett
          </p>
          <p className="font-rajdhani text-gray-400 text-base md:text-lg max-w-lg leading-relaxed mb-8">
            Tactical shooter veteran. Strategic mastermind. Proving age is just a number on the battlefield since 2008.
            <strong className="text-red-500"> Don&apos;t let the grey fool you &mdash; Old Man Murks still hits.</strong>
          </p>

          <div className="flex flex-wrap gap-6 mb-10">
            {[
              { label: "K/D Ratio", value: "3.8" },
              { label: "Wins", value: "1,240+" },
              { label: "Hours Played", value: "12K+" },
            ].map((stat) => (
              <div key={stat.label} className="flex flex-col">
                <span className="font-bebas text-4xl text-red-500">{stat.value}</span>
                <span className="font-rajdhani text-gray-500 text-xs uppercase tracking-widest">{stat.label}</span>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-4">
            <button
              onClick={() => scrollTo("highlights")}
              className="font-rajdhani font-bold uppercase tracking-widest text-sm bg-red-700 hover:bg-red-600 text-white px-8 py-3.5 rounded-sm transition-all duration-300 cursor-pointer whitespace-nowrap border border-red-600 hover:shadow-lg hover:shadow-red-900/50"
            >
              View Highlights
            </button>
            <button
              onClick={() => scrollTo("contact")}
              className="font-rajdhani font-bold uppercase tracking-widest text-sm bg-transparent hover:bg-white/10 text-white px-8 py-3.5 rounded-sm transition-all duration-300 cursor-pointer whitespace-nowrap border border-white/30 hover:border-white/60"
            >
              Get In Touch
            </button>
          </div>

          <div className="flex items-center gap-4 mt-8">
            {[
              { icon: "ri-twitch-fill", color: "text-gray-300 hover:text-red-400" },
              { icon: "ri-youtube-fill", color: "text-gray-300 hover:text-red-400" },
              { icon: "ri-twitter-x-fill", color: "text-gray-300 hover:text-red-400" },
              { icon: "ri-instagram-fill", color: "text-gray-300 hover:text-red-400" },
            ].map((s) => (
              <button
                key={s.icon}
                onClick={() => scrollTo("social")}
                className={`w-9 h-9 flex items-center justify-center text-xl ${s.color} transition-all cursor-pointer`}
              >
                <i className={s.icon} />
              </button>
            ))}
          </div>
        </div>

        {/* Character art panel — grayscale to match B&W world */}
        <div
          className={`flex-shrink-0 relative transition-all duration-1000 delay-300 ${visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"}`}
        >
          {/* Glow behind character */}
          <div className="absolute -inset-6 bg-white/5 blur-3xl rounded-full" />

          <div className="relative w-72 md:w-96 h-96 md:h-[540px] overflow-hidden">
            {/* Corner frame accents */}
            <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-red-600 z-20" />
            <div className="absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 border-red-600 z-20" />
            <div className="absolute bottom-0 left-0 w-8 h-8 border-b-2 border-l-2 border-red-600/40 z-20" />
            <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-red-600/40 z-20" />

            <img
              src="https://static.readdy.ai/image/c088ed944a840dff525f6c9a83fbd586/7be84091aa02310197fc44170c112371.png"
              alt="Old Man Murks - Mike Barrett"
              className="w-full h-full object-cover object-top"
              style={{ filter: "grayscale(100%) contrast(1.15) brightness(1.0)" }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />

            {/* Name badge */}
            <div className="absolute bottom-4 left-4 right-4 bg-black/80 backdrop-blur-sm border-l-2 border-red-600 px-4 py-2">
              <p className="font-bebas text-white text-lg tracking-widest">MIKE BARRETT</p>
              <p className="font-rajdhani text-red-500 text-xs uppercase tracking-widest">aka Old Man Murks</p>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
        <span className="font-rajdhani text-gray-600 text-xs uppercase tracking-widest">Scroll Down</span>
        <div className="w-0.5 h-8 bg-gradient-to-b from-red-600 to-transparent" />
      </div>
    </section>
  );
}
